import type { FC } from 'react';
import { useRouter } from 'next/router';
import { LanguageSwitch32 } from '@packages/themes/icons';
import type { CookieOptions } from '@packages/shared';
import { useConfig } from '@packages/shared';
import { localizedPathnameCacheKey } from '@packages/config/src/default';
import { getFullLocale } from '@packages/config';
import useSWR from 'swr';
import { setCookie } from 'nookies';
import type { SlotProps } from './Slot';
import { Slot } from './Slot';

export type LocaleSwitchSlotProps = Pick<SlotProps, 'sx'>;

/** Locale Switch slot with list of helpers in popover */
export const LocaleSwitchSlot: FC<LocaleSwitchSlotProps> = ({ sx }) => {
  const {
    i18n: { languages },
  } = useConfig();
  const { locale: language, asPath, push } = useRouter();
  const targetLanguage: string = languages.find((l: string) => l !== language)!;
  const { data: localizedPathname } = useSWR(localizedPathnameCacheKey, {
    // prevent fetching newer data and use only stale data (https://swr.vercel.app/docs/revalidation#disable-automatic-revalidations)
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });

  const COOKIE_OPTIONS: CookieOptions = {
    path: '/',
    maxAge: 86400, // 90 days in seconds
    secure: false, // true would be nice but this is what intershop defines,
    sameSite: 'lax',
    httpOnly: false,
  };
  return (
    <Slot
      icon={<LanguageSwitch32 />}
      title="fr / de"
      locale={targetLanguage}
      href={localizedPathname || asPath}
      useRouterLink={false}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        setCookie(null, 'PreferredLanguage', targetLanguage, COOKIE_OPTIONS);
        // Set PreferredLocale with legacy parameters for consistent behaviour with the legacy checkout.
        // Can be removed after nextjs checkout is live
        setCookie(null, 'PreferredLocale', getFullLocale(targetLanguage).replace('-', '_'), {
          ...COOKIE_OPTIONS,
          domain: `.${window.document.domain}`,
        });
        push(localizedPathname || asPath, localizedPathname || asPath, { locale: targetLanguage });
      }}
      sx={sx}
    />
  );
};
